import { Locale, LocalizedText } from '../types';

export interface UiDictionary {
  nav: {
    apps: string;
    support: string;
    about: string;
    contact: string;
    allApps: string;
    viewDetails: string;
  };
  home: {
    heroBadge: string;
    heroTitle: string;
    heroSubtitle: string;
    heroCtaPrimary: string;
    heroCtaSecondary: string;
    showcaseTitle: string;
    showcaseSubtitle: string;
    philosophyTitle: string;
    supportBannerTitle: string;
    supportBannerDesc: string;
    supportBannerCta: string;
  };
  appDetail: {
    featuresTitle: string;
    screenshotsTitle: string;
    screenshotsSubtitle: string;
    lightboxLabel: string;
    lightboxOpen: string;
    lightboxClose: string;
    lightboxPrev: string;
    lightboxNext: string;
    targetAudienceTitle: string;
    downloadTitle: string;
    faqTitle: string;
    supportLinkText: string;
    privacyLinkText: string;
    termsLinkText: string;
  };
  support: {
    title: string;
    subtitle: string;
    officialBadge: string;
    lastUpdatedPrefix: string;
    versionPrefix: string;
    quickCategoriesTitle: string;
    faqTitle: string;
    directContactTitle: string;
    directContactDesc: string;
    responseTimeText: string;
    accountDeletionTitle: string;
    accountDeletionDesc: string;
  };
  common: {
    downloadAppStore: string;
    downloadGooglePlay: string;
    privacyPolicy: string;
    termsOfService: string;
    copyright: string;
    notFoundTitle: string;
    notFoundDesc: string;
    backHome: string;
    emailLabel: string;
    contactUs: string;
  };
}

export const DICTIONARY: Record<Locale, UiDictionary> = {
  ko: {
    nav: {
      apps: '앱 소개',
      support: '고객지원',
      about: '회사 소개',
      contact: '문의하기',
      allApps: '전체 앱 둘러보기',
      viewDetails: '자세히 보기',
    },
    home: {
      heroBadge: '모바일 소프트웨어 스튜디오',
      heroTitle: '일상을 더 편리하게 만드는 모바일 경험을 개발합니다.',
      heroSubtitle: 'Project Kapital은 일상의 가치를 높이는 모바일 서비스를 직접 기획하고 개발·운영하는 독립 소프트웨어 스튜디오입니다.',
      heroCtaPrimary: '서비스 중인 앱 둘러보기',
      heroCtaSecondary: '회사 소개',
      showcaseTitle: '현재 서비스 중인 모바일 앱',
      showcaseSubtitle: '안정적인 기술력과 세심한 디자인으로 일상의 생산성과 편리함을 더합니다.',
      philosophyTitle: '우리가 일하는 방식과 철학',
      supportBannerTitle: '운영 중인 앱에 문의나 문제가 있으신가요?',
      supportBannerDesc: '스토어 공식 지원 센터를 통해 계정 문제, 데이터 백업, 기능 사용 방법 및 1:1 문의를 신속하게 해결하실 수 있습니다.',
      supportBannerCta: '공식 고객지원 센터 바로가기',
    },
    appDetail: {
      featuresTitle: '주요 핵심 기능',
      screenshotsTitle: '한눈에 보는 앱 컨셉',
      lightboxLabel: '앱 컨셉 이미지 크게 보기',
      lightboxOpen: '크게 보기',
      lightboxClose: '닫기',
      lightboxPrev: '이전 이미지',
      lightboxNext: '다음 이미지',
      screenshotsSubtitle: '주요 기능을 이해하기 쉽게 담은 컨셉 이미지예요. 실제 앱 화면은 업데이트에 따라 조금씩 다를 수 있어요.',
      targetAudienceTitle: '이런 분들에게 추천합니다',
      downloadTitle: '지금 바로 시작하세요',
      faqTitle: '자주 묻는 질문 (FAQ)',
      supportLinkText: '공식 지원 센터 바로가기',
      privacyLinkText: '개인정보처리방침',
      termsLinkText: '이용약관',
    },
    support: {
      title: '공식 고객지원 센터',
      subtitle: '사용 중 궁금하신 점이나 발생한 문제를 신속하게 해결해 드립니다.',
      officialBadge: '스토어 공인 지원 센터 (Official Support)',
      lastUpdatedPrefix: '최종 업데이트:',
      versionPrefix: '지원 버전:',
      quickCategoriesTitle: '빠른 문제 해결 카테고리',
      faqTitle: '자주 묻는 질문',
      directContactTitle: '직접 문의하기',
      directContactDesc: 'FAQ로 해결되지 않은 문제는 공식 지원 이메일로 접수해 주시면 개발팀이 신속히 확인하여 답변드립니다.',
      responseTimeText: '영업일 기준 24~48시간 이내에 성심껏 회신드립니다.',
      accountDeletionTitle: '계정 및 데이터 삭제 요청 안내',
      accountDeletionDesc: '사용자의 개인정보 및 계정 데이터를 영구 삭제하고자 하실 경우, 지원 이메일로 가입 계정 식별 정보를 기재하여 요청해 주시면 관련 법령 및 정책에 따라 즉시 파기 처리됩니다.',
    },
    common: {
      downloadAppStore: 'App Store에서 다운로드',
      downloadGooglePlay: 'Google Play에서 다운로드',
      privacyPolicy: '개인정보처리방침',
      termsOfService: '이용약관',
      copyright: '© 2026 Kapitalletter. All rights reserved.',
      notFoundTitle: '페이지를 찾을 수 없습니다',
      notFoundDesc: '요청하신 페이지가 존재하지 않거나 잘못된 경로입니다.',
      backHome: '홈으로 돌아가기',
      emailLabel: '공식 지원 이메일',
      contactUs: '문의하기',
    },
  },
  en: {
    nav: {
      apps: 'Apps',
      support: 'Support',
      about: 'About',
      contact: 'Contact',
      allApps: 'Explore All Apps',
      viewDetails: 'View Details',
    },
    home: {
      heroBadge: 'Mobile Software Studio',
      heroTitle: 'Crafting thoughtful mobile experiences for everyday life.',
      heroSubtitle: 'Project Kapital is an independent software studio designing, building, and operating high-quality iOS and Android applications.',
      heroCtaPrimary: 'Explore Our Apps',
      heroCtaSecondary: 'About Us',
      showcaseTitle: 'Active Mobile Applications',
      showcaseSubtitle: 'Built with solid engineering and attentive design to bring productivity to daily life.',
      philosophyTitle: 'Our Engineering Philosophy',
      supportBannerTitle: 'Need assistance with one of our apps?',
      supportBannerDesc: 'Access our official store support center for troubleshooting guides, account recovery, and direct developer communication.',
      supportBannerCta: 'Visit Official Support Hub',
    },
    appDetail: {
      featuresTitle: 'Key Features',
      screenshotsTitle: 'App Concept at a Glance',
      lightboxLabel: 'Enlarged app concept image',
      lightboxOpen: 'View larger',
      lightboxClose: 'Close',
      lightboxPrev: 'Previous image',
      lightboxNext: 'Next image',
      screenshotsSubtitle: 'Concept images that illustrate the key features. Actual in-app screens may vary as the app is updated.',
      targetAudienceTitle: 'Designed For You If',
      downloadTitle: 'Get Started Today',
      faqTitle: 'Frequently Asked Questions',
      supportLinkText: 'Visit Official Support Page',
      privacyLinkText: 'Privacy Policy',
      termsLinkText: 'Terms of Service',
    },
    support: {
      title: 'Official Support Center',
      subtitle: 'Find troubleshooting guides, answers to common questions, or contact the developer team directly.',
      officialBadge: 'Store-Verified Support Center',
      lastUpdatedPrefix: 'Last updated:',
      versionPrefix: 'Supported version:',
      quickCategoriesTitle: 'Quick Troubleshooting Categories',
      faqTitle: 'Frequently Asked Questions',
      directContactTitle: 'Contact Developer Directly',
      directContactDesc: 'If your issue is not resolved through the FAQ, reach out via our official support email. Our team will assist you promptly.',
      responseTimeText: 'We typically respond within 24–48 business hours.',
      accountDeletionTitle: 'Account & Data Deletion Guide',
      accountDeletionDesc: 'To request permanent deletion of your account and personal data, email our support address with your account identifier. We will purge your records in compliance with privacy regulations.',
    },
    common: {
      downloadAppStore: 'Download on the App Store',
      downloadGooglePlay: 'Get it on Google Play',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      copyright: '© 2026 Kapitalletter. All rights reserved.',
      notFoundTitle: 'Page Not Found',
      notFoundDesc: 'The page or application you requested could not be found.',
      backHome: 'Back to Home',
      emailLabel: 'Official Support Email',
      contactUs: 'Contact Us',
    },
  },
};

/**
 * Resolves a LocalizedText object into string according to active locale.
 * Always falls back to Korean if secondary locale translation is absent.
 */
export function resolveLocalizedText(text: LocalizedText, locale: Locale): string {
  if (locale === 'en' && text.en && text.en.trim() !== '') {
    return text.en;
  }
  return text.ko;
}
